import React from 'react';

const english = {
  // Base configuration translations
  baseConfig: {
    baselineSuccessPercent: "Baseline Success Percentage",
    baseSuccessRateDescription: "This is your estimate of the base likelihood of success for your startup concept. Industry averages are typically in the 10-20% range.",
    locationTitle: "Location",
    locationOptions: {
      stcharles: {
        name: "St. Charles",
        description: "Standard cost of living with no multiplier applied to expenses."
      },
      sanfrancisco: {
        name: "San Francisco",
        description: "All salary, operating, and environment costs are multiplied by 1.64x to account for the higher cost of living and operating in the Bay Area."
      }
    },
    hideInfoLabel: "Hide Info",
    moreInfoLabel: "Info",
    locationInfo: (
      <div className="bg-blue-50 p-3 rounded mb-3 text-sm">
        <p className="mb-2"><strong>St. Louis:</strong> Standard cost of living with no multiplier applied to
          expenses.</p>
        <p><strong>San Francisco:</strong> All salary, operating, and environment costs are multiplied by 1.64x
          to account for the <a
            href="https://www.numbeo.com/cost-of-living/compare_cities.jsp?country1=United+States&city1=Saint+Louis%2C+MO&country2=United+States&city2=San+Francisco%2C+CA"
            className="text-blue-600 underline font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            higher cost of living and operating in the Bay Area compared to the Saint Louis Area
          </a>.
        </p>
      </div>
    )
  },

  // Location multiplier translations
  locationMultiplier: {
    title: "Cost Multiplier",
    hideInfoLabel: "Hide Info",
    showInfoLabel: "Info",
    description: "This multiplier is applied to salaries, work environment, and operating costs when San Francisco is selected as your location.",
    multiplierSuffix: "× multiplier applied to costs",
    costOfLivingLink: {
      url: "https://www.numbeo.com/cost-of-living/compare_cities.jsp?country1=United+States&city1=Saint+Louis%2C+MO&country2=United+States&city2=San+Francisco%2C+CA",
      text: "View cost of living comparison between St. Louis and San Francisco"
    }
  },

  // Salary tiers
  salaryTiers: [
    {
      amount: 55000,
      modifier: -3.0,
      description: 'Tier 0: $55K',
      detailedDescription: 'Tier 0 - Strained relationships, increased chance of conflicts or burnout, bills likely to exceed income.'
    },
    {
      amount: 70000,
      modifier: 0.0,
      description: 'Tier 1: $70K',
      detailedDescription: 'Tier 1 - Basic living, medium to high stress, inability to progress and may fall behind on paying off debts, medium amount of energy expended worrying about money.'
    },
    {
      amount: 85000,
      modifier: 0.7,
      description: 'Tier 2: $85K',
      detailedDescription: 'Tier 2 - Stable lifestyle, improved focus, low stress, slow but steady progress towards paying off debt.'
    },
    {
      amount: 100000,
      modifier: 1.2,
      description: 'Tier 3: $100K',
      detailedDescription: 'Tier 3 - Full support, high clarity, no stress.'
    }
  ],

  // Salary selector translations
  salarySelector: {
    hideDetailsLabel: "Hide Info",
    showDetailsLabel: "Info",
    tierPrefix: "Tier",
    monthlyLabel: "Monthly Cost",
    monthlyBaseLabel: "Monthly Base Cost",
    monthlyAdjustedLabel: "Monthly Adjusted Cost",
    annualLabel: "Annual Cost",
    annualBaseLabel: "Annual Base Cost",
    annualAdjustedLabel: "Annual Adjusted Cost",
    adjustedLabel: "Adjusted Cost",
    successModifierLabel: "Success Modifier",
    salaryImpactText: "Salary affects team morale, productivity, and overall stability. Higher tiers provide financial security and reduce stress.",
    eaAdjustmentText: "When an Executive Assistant is hired, Tristen's salary is reduced by $20,000 due to money saved on food delivery and errands.",
    eaDiscountText: "with EA discount",
    inSFText: "in SF",
    eaSavingsAmount: 20000
  },

  // Core team translations
  coreTeam: {
    tristenInfo: {
      title: "Tristen's Salary Tier",
      description: "Tristen's salary affects team morale, productivity, and overall stability. Higher tiers provide financial security and reduce stress."
    },
    martinInfo: {
      title: "Martin's Salary Tier",
      description: "Martin's salary affects team morale, productivity, and overall stability. Higher tiers provide financial security and reduce stress."
    },
    eaSavingsAmount: 20000,
    title: "Founders Salary"
  },

  // Work environment translations
  environment: {
    title: "Work Environment",
    costLabel: "Cost",
    freeLabel: "Free",
    monthlyCostLabel: "Monthly Cost",
    annualCostLabel: "Annual Cost",
    successModifierLabel: "Success Modifier",
    showDetailsLabel: "Info",
    hideDetailsLabel: "Hide Info",
    environmentDescriptionLabel: "Your work environment affects team productivity, professional image, and networking opportunities.",
    annualCostLabel2: "Annual Cost:",
    perYearLabel: "/year"
  },

  // Operating resources translations
  operatingResources: {
    title: "Operating Resources",
    description: "Operating resources determine how quickly and effectively you can execute on opportunities and respond to challenges.",
    costLabel: "Cost",
    freeLabel: "Free",
    monthlyCostLabel: "Monthly Cost",
    annualCostLabel: "Annual Cost",
    successModifierLabel: "Success Modifier",
    showDetailsLabel: "Info",
    hideDetailsLabel: "Hide Info",
    environmentDescriptionLabel: "Your work environment affects team productivity, professional image, and networking opportunities.",
    annualCostLabel2: "Annual Cost:",
    perYearLabel: "/year",
    inSFText: "in SF",
    sfShortText: "SF",
    perMonthText: "/month"
  },

  // Work environment options - St. Charles
  stCharlesWorkEnvironments: [
    {
      name: 'garage',
      cost: 0,
      modifier: 0.0,
      description: 'Garage (Home Base): $0/year',
      detailedDescription: 'Working from Tristen\'s garage. No additional costs, but limited professional atmosphere. Neutral to net negative impact on success rate. Neutral from Fall to Spring, but negative in the Missouri Summer. AC struggles to cool garage more than a few degrees below outside temperature. Humidity reaches 100% which is probably bad for the mini-pc\'s and hardware. Coworkers are bees, wasps, ants, and spiders.'
    },
    {
      name: 'coworking',
      cost: 4800,
      modifier: 0.5,
      description: 'Co-working space at local business incubator: $4,800/year',
      detailedDescription: 'Access to shared coworking facilities and networking opportunities within a local business incubator that provides office and retail space for over 70 startups all within a 5 block radius.'
    },
    {
      name: 'office',
      cost: 24000,
      modifier: 1.0,
      description: 'Private Office at local business incubator: $24,000/year',
      detailedDescription: 'Dedicated private office within a local business incubator that provides office and retail space for over 70 startups in a great location surrounded by eateries. High-speed fiber access because the building directly across the street used to be a data-center. Room and power for server racks and networking equipment. Space to accommodate up to a 15 person company. Likely to only be available for a limited time, space is subsidized for startup companies.'
    }
  ],

  // Operating resources options
  operatingResourcesOptions: [
    {
      name: 'survival',
      costPerMonth: 0,
      modifier: -3.0,
      description: 'Survival: $0/month',
      detailedDescription: 'No budget for tools, subscriptions, or services. Severely limits adaptability, lowers morale, and creates a feeling of stagnation.'
    },
    {
      name: 'sustainable',
      costPerMonth: 3000,
      modifier: 0.0,
      description: 'Sustainable: $3k/month',
      detailedDescription: 'Covers basic software, essential services, and occasional contractor support. Enables moderate productivity and problem-solving.'
    },
    {
      name: 'enabled',
      costPerMonth: 5000,
      modifier: 0.3,
      description: 'Enabled: $5k/month',
      detailedDescription: 'Provides access to premium tools, regular professional services, and better networking capacity. Supports steady growth and resilience.'
    },
    {
      name: 'empowered',
      costPerMonth: 10000,
      modifier: 0.8,
      description: 'Empowered: $10k/month',
      detailedDescription: 'Robust budget for part-time specialists, top-tier tools, and rapid response capabilities. Enables high flexibility and strategic execution.'
    }
  ],

  // Hires translations
  hires: {
    hiresLabel: "Hires",
    showDetailsLabel: "Info",
    hideDetailsLabel: "Hide Info",
    equityLabel: "Equity: ",
    salaryLabel: "Salary: ",
    noneLabel: "None",
    equityCalculationText: "Equity is automatically calculated based on salary:",
    atLabel: " at",
    noEquityForContractorsText: "No equity for contractors",
    contractorText: "contractor",
    currentLabel: "Current",
    withHoursText: "with",
    hoursText: "hours",
    percentSymbol: "%",
    fullTimeEntireYearText: "Full-time, entire year",
    hrsWeekText: "hrs/week",
    fteText: "FTE",
    entireYearText: "entire year",
    monthsText: "months",
    multiPeriodText: "Multi-period",
    avgText: "avg",
    periodsText: "periods",
    designer: {
      title: "UI/UX Designer",
      successImpact: "+0.0-5.0% success",
      description: (
        <div className="bg-blue-50 p-3 rounded mb-3 text-sm">
          <p>
            A strong UI/UX designer can meaningfully enhance usability and product appeal—creating a
            competitive edge through experience. While we can move forward without one, having the right
            designer early on could significantly raise our execution quality, especially considering
            our roadmap for the next year and the ways in which we intend to establish a moat.
          </p>
          <p className="mt-2">
            This role would combine product thinking, wireframing, and visual design, with an ability to
            support lightweight web development as needed. They'd help translate concepts into Figma
            mockups, improve user flows, run A/B tests, and gather feedback through early user research
            and onboarding observation—especially with enterprise clients. Their impact depends heavily
            on cross-functional collaboration and design maturity.
          </p>
        </div>
      ),
      maxSalary: 140000,
      equityCalculation: {
        min: 0,
        max: 2,
        formula: (salary) => Math.max(0, Math.min(2, 2 - (salary / 70000)))
      }
    },
    ea: {
      title: "Executive Assistant",
      successImpact: "+0.0-7.0% success",
      description: (
        <div className="bg-blue-50 p-3 rounded mb-3 text-sm">
          <p>
            Tristen is a highly capable founder managing bipolar disorder, ADHD, and autism. During
            periods of intense focus or emotional variability, basic routines—like eating, taking
            medication, or following up on logistics—can fall by the wayside. During these times
            structured support is critical to maintaining his momentum without compromising mental or
            physical well-being. This role supports Tristen and the broader team by ensuring that
            operational and interpersonal systems run smoothly and nothing important slips through the
            cracks.
          </p>

          <p className="mt-2">
            <strong>Executive Assistant:</strong>
            This is a high-trust, high-impact operational role designed to support both Tristen and the
            team. The EA helps Tristen stay on track with personal systems—meals, medication,
            appointments, schedule, and inbox—while also handling internal logistics, meeting
            coordination, and communication support. They reduce context switching, enforce routine, and
            act as a force multiplier across the company.
          </p>

          <p className="mt-2">
            <strong>Organizational Psychologist Executive Assistant & Coach:</strong>
            This is a high-leverage operational role for someone with organizational psychology
            expertise who thrives in fast-moving, early-stage environments. While functioning as an
            Executive Assistant and handling everything described above, they also provide deep
            behavioral insight and leadership support. They help Tristen stay grounded and aligned
            during periods of high intensity or emotional variability, offering structured reflection,
            systems thinking, and emotional pattern recognition.
          </p>

          <p className="mt-2">
            For an autistic CEO, an organizational psychologist who can function as an EA offers
            especially powerful value. Their presence in meetings and team interactions allows them to
            observe subtle dynamics, reduce miscommunication, and support clearer collaboration. By
            understanding the unique ways Tristen processes information, stress, and relationships, they
            help create a more adaptive leadership style and a psychologically safe company culture.
          </p>

          <p className="mt-2">
            This isn't a back-office support role—it's a startup-native form of embedded leadership
            infrastructure. It combines execution and insight, helping Tristen maintain operational
            momentum while reinforcing emotional resilience and value-aligned decision-making. The
            result is a more sustainable, stable leadership foundation for both the founder and the
            company over time.
          </p>
          <p className="mt-2">
            <strong>Salary Impact:</strong> Hiring an EA reduces Tristen's salary by $20,000, based
            on cost savings from food delivery, missed appointments, and administrative inefficiencies.
          </p>
        </div>
      ),
      types: {
        regular: {
          title: "Regular EA",
          successImpact: "+0.0-2.0% success",
          maxSalary: 60000,
          equityCalculation: {
            min: 0,
            max: 0.5,
            formula: (salary) => Math.max(0, Math.min(0.5, 0.5 - (salary / 120000)))
          }
        },
        clinical: {
          title: "Organizational Psychologist EA & Coach",
          successImpact: "+0.0-7.0% success",
          maxSalary: 160000,
          equityCalculation: {
            min: 0,
            max: 2,
            formula: (salary) => Math.max(0, Math.min(2, 2 - (salary / 80000)))
          }
        }
      },
      tristenSavings: 20000,
      savingsDescription: "Note: Hiring an EA reduces Tristen's salary by $20,000, based on cost savings from food delivery, missed appointments, and administrative inefficiencies."
    },
    social: {
      title: "Social Media & Marketing Strategist",
      successImpact: "+0.0-1.0% success",
      description: (
        <div className="bg-blue-50 p-3 rounded mb-3 text-sm">
          <p>
            A skilled Social Media & Marketing Strategist creates immediate brand momentum by
            documenting your startup journey from day one—establishing legitimacy that Enterprise
            clients require while building an authentic narrative that attracts top-tier talent as you
            scale. This consistent professional presence signals stability and competence to potential
            enterprise partners who need reassurance before committing to newer vendors.
          </p>
          <p className="mt-2">
            This role directly offsets Tristen's time by taking ownership of the execution loop: Tristen
            can provide quick creative direction and approve concepts in minutes rather than spending
            hours on implementation. They'll transform rough ideas into polished campaigns, manage the
            content calendar, and maintain momentum without constant founder involvement—freeing Tristen
            to focus on product and enterprise relationships.
          </p>
          <p className="mt-2">
            Leveraging Tristen's state championship marketing background, they'll establish your company
            voice, capture behind-the-scenes moments, and craft stories that demonstrate longevity and
            purpose. The social foundation they build makes both talent acquisition and audience growth
            significantly easier than starting from zero later, when these efforts would compete with
            more critical scaling priorities.
          </p>
        </div>
      ),
      maxSalary: 60000,
      equityCalculation: {
        min: 0,
        max: 0.75,
        formula: (salary) => Math.max(0, Math.min(0.75, 0.75 - (salary / 80000)))
      }
    }
  },

  // Hire Options translations
  hireOptions: {
    multiPeriodTitleLabel: "Work Configuration Timeline",
    hoursPerWeekLabel: "Hours per week",
    employmentTypeLabel: "Employment Type",
    employeeLabel: "Employee",
    contractorLabel: "Contractor",
    periodStartLabel: "Start Month",
    periodEndLabel: "End Month",
    immediateHireLabel: "Start (Month 0)",
    yearEndLabel: "End of Year (Month 12)",
    monthLabel: "Month",
    monthsLabel: "Months",
    fullYearLabel: "Full Year",
    followedByLabel: "Followed by →",
    addPeriodLabel: "+ Add Another Period",
    removeLabel: "Remove Period",
    maxPeriodsNoteLabel: "({current}/{max} periods)",
    periodHelpText: "Define different working arrangements throughout the year with multiple periods.",
    contractorCostNote: "(+50% cost, no equity)",
    hireTitleLabel: "Work Configuration",
    hireStartLabel: "Start Month",
    partialYearNote: "Working {months} months this year",
    hoursRangeMin: "5",
    hoursRangeMax: "40",
    hourMarkers: ["5", "10", "15", "20", "25", "30", "35", "40"]
  },

  // Hire Config Period translations
  hireConfigPeriod: {
    startLabel: "Start",
    endLabel: "End",
    hoursPerWeekLabel: "Hours per week",
    employmentTypeLabel: "Employment Type",
    removeLabel: "Remove Period",
    followedByLabel: "Followed by next period →",
    automaticallySetText: "(automatically set)",
    fullYearLabel: "Full Year",
    monthsLabel: "Months",
    monthLabel: "Month",
    immediateHireLabel: "Start (Month 0)",
    yearEndLabel: "End of Year (Month 12)",
    cannotRemoveOnlyPeriodTitle: "Cannot remove the only period",
    hoursRangeMin: "5",
    hoursRangeMax: "40"
  },

  // Multi Period Config translations
  multiPeriodConfig: {
    multiPeriodTitleLabel: "Work Configuration Timeline",
    periodHelpText: "Use multiple periods to define different working arrangements throughout the year.",
    addPeriodLabel: "+ Add Another Period",
    maxPeriodsNoteLabel: "({current}/{max} periods)",
    totalAnnualCostLabel: "Total Annual Cost:",
    hrsWeekShortText: "h/wk",
    mosShortText: "mos",
    monthsLabel: "Months",
    costLabel: "Cost",
    monthLabels: ["0", "3", "6", "9", "12"]
  },

  // Hire Cost Summary translations
  hireCostSummary: {
    workScheduleLabel: "Work Schedule",
    fullTimeText: "Full-time",
    partTimeText: "Part-time",
    hoursPerWeekFormat: "{hours} hrs/week",
    monthlyCostLabel: "Monthly Cost",
    annualCostLabel: "Annual Cost",
    equityLabel: "Equity",
    tristenSavingsLabel: "Tristen Salary Savings",
    successModifierLabel: "Success Modifier",
    hrsWeekShortText: "h/wk"
  },

  // Additional Costs translations
  additionalCosts: {
    title: "Additional Costs",
    hideInfoLabel: "Hide Info",
    showInfoLabel: "Info",
    aboutTitle: "About Additional Costs",
    mainDescription: "These expenses don't directly affect your success rate but must be included in your total budget:",
    legalTitle: "Legal Costs:",
    legalLabel: "Legal Costs",
    legalDescription: "Formation, contracts, IP protection, compliance",
    legalFullDescription: "Entity formation, contracts, IP protection, compliance. Typically $2,000-$10,000 for early-stage startups.",
    legalPlaceholder: "Enter expected legal costs",
    travelTitle: "Travel Costs:",
    travelLabel: "Travel Costs",
    travelDescription: "Customer visits, conferences, investor meetings",
    travelFullDescription: "Customer visits, conferences, investor meetings. Budget based on your specific needs.",
    travelPlaceholder: "Enter expected travel costs",
    additionalTitle: "Additional Costs:",
    additionalLabel: "Additional Costs",
    additionalDescription: "Other miscellaneous expenses",
    additionalFullDescription: "Any other expenses not covered elsewhere (equipment, insurance, etc.).",
    additionalPlaceholder: "Enter any additional costs",
    miscDescription: "Equipment, insurance, and other miscellaneous expenses"
  },

  // Summary Box translations
  summaryBox: {
    costLabel: "Cost",
    savingsLabel: "Savings",
    equityLabel: "Equity",
    successLabel: "Success",
    neutralLabel: "Info"
  },

  // Results Display translations
  results: {
    title: "Results",
    costBreakdownTitle: "Cost Breakdown",
    successProbabilityTitle: "Success Probability",
    hideDetailsLabel: "Hide Details",
    showDetailsLabel: "Show Details",
    foundersSalariesLabel: "Founders Salaries",
    hiresLabel: "Hires",
    workEnvironmentLabel: "Work Environment",
    operatingResourcesLabel: "Operating Resources",
    additionalCostsLabel: "Additional Costs",
    equityCostsLabel: "Equity Costs",
    totalAnnualCostLabel: "Total Annual Cost",
    totalEquityCostLabel: "Total Equity Cost",
    baselineRateLabel: "Baseline Rate",
    fixedFactorModifiersLabel: "Fixed Factor Modifiers",
    potentialHireImpactLabel: "Potential Hire Impact",
    estimatedSuccessRateLabel: "Estimated Success Rate",
    successRangeLabel: "Success Range",
    interpretationLabel: "Interpretation",
    basedOnAvgLabel: "Based on {avg}% average",
    cultureScoreLabel: "Culture Score",
    burnScoreLabel: "Burn Score",
    cultureFactorsLabel: "Culture Factors",
    teamSizeLabel: "Team Size",
    workEnvironmentLabel2: "Work Environment",
    founderComfortLabel: "Founder Comfort",
    operatingResourcesLabel2: "Operating Resources",
    eaSupportLabel: "EA Support",
    keyMetricsLabel: "Key Metrics",
    annualBurnRateLabel: "Annual Burn Rate",
    monthlyBurnLabel: "Monthly Burn",
    totalEquityGivenLabel: "Total Equity Given",
    monthsLabel: "months",
    costBreakdownDescription: `This breakdown shows all costs associated with your startup configuration on an annual basis.`,
    successBreakdownDescription: `
      Baseline Rate: The starting success probability you defined for your startup concept.
      Fixed Factors: Modifiers from core team salaries, work environment, and operating resources.
      Potential Hire Impact: The range of additional success probability that could be gained from your selected hires, depending on their performance and integration.
      Average Success Rate: The mean value between minimum and maximum success rates, representing the expected outcome.
      Success Range: Shows both the minimum guaranteed success rate and the maximum potential if all hires perform optimally.
    `,
    sanFranciscoNote: (multiplier) => `A ${multiplier}x cost of living multiplier has been applied to salaries, work environment, and operating costs since you selected San Francisco as your location.`,
    membersLabel: "members",
    workEnvironmentLevels: {
      garage: "Basic",
      coworking: "Collaborative",
      office: "Professional"
    },
    tierLabel: "Tier",
    operatingResourceLevels: {
      survival: "Minimal",
      sustainable: "Basic",
      enabled: "Good",
      empowered: "Excellent"
    },
    eaSupportFormat: {
      regular: "+5 (Regular)",
      clinical: "+15 (Org Psych)"
    },

    // Itemized receipt labels
    salaryTierLabel: "Salary Tier:",
    amountLabel: "Amount:",
    eaSavingsLabel: "EA Savings:",
    locationLabel: "Location:",
    sanFranciscoText: "San Francisco",
    salaryLabel: "Salary:",
    employmentTypeLabel: "Employment Type:",
    scheduleLabel: "Schedule:",
    periodLabel: "Period",
    contractorText: "Contractor (+50%)",
    employeeText: "Employee",
    founderText: "Founder",
    tristenFounderLabel: "Tristen (Founder)",
    martinFounderLabel: "Martin (Founder)",
    designerLabel: "UI/UX Designer",
    eaClinicalLabel: "Org Psych EA & Coach:",
    eaRegularLabel: "Executive Assistant:",
    socialMediaLabel: "Social Media Manager:",
    annualCostLabel: "Annual Cost:",
    monthlyCostLabel: "Monthly Cost:",
    legalCostsLabel: "Legal Costs:",
    travelCostsLabel: "Travel Costs:",
    otherCostsLabel: "Other Costs:",
    entireYearText: "entire year",
    monthsText: "months",
    perYearText: "/year",
    perMonthText: "/month",
    hoursPerWeekText: "hrs/week",
    additionalCostsBreakdownLabel: "Additional Costs Breakdown",
    equityDistributionLabel: "Equity Distribution",
    hrsWeekShortText: "h/wk",
    mosShortText: "mos",

    // Success interpretation texts
    interpretations: {
      highRisk: "High Risk: Your current configuration has a very low chance of success. Consider adjusting key factors.",
      challenging: "Challenging: Below average success probability. Look for areas to improve your configuration.",
      average: "Average: Your configuration has a typical startup success rate.",
      aboveAverage: "Above Average: Your configuration is positioned better than most startups.",
      excellent: "Excellent: Your configuration has a significantly higher chance of success than the average startup."
    },

    // Schedule display text
    fullTimeEntireYearText: "Full-time, entire year",
    multiPeriodText: "Multi-period",
    avgText: "avg",
    periodsText: "periods",
    fteText: "FTE",

    // Location display
    sfDisplayText: "San Francisco",
    multiplierSymbolText: "×",

    // Designer titles and impact
    designerTitleText: "UI/UX Designer",
    designerImpactText: "+0.0-5.0%",

    // EA titles and impact
    eaRegularTitleText: "Executive Assistant",
    eaRegularImpactText: "+0.0-2.0%",
    eaOrgPsychTitleText: "Org Psych EA & Coach",
    eaOrgPsychImpactText: "+0.0-7.0%",

    // Social Media titles and impact
    socialMediaTitleText: "Social Media Manager",
    socialMediaImpactText: "+0.0-1.0%",

    // More localized strings for culture and metrics section
    interpretationTitle: "Interpretation",
    cultureScoreTitle: "Culture Score",
    burnScoreTitle: "Burn Score",
    cultureFactorsTitle: "Culture Factors",
    keyMetricsTitle: "Key Metrics",
    teamMembersText: "members",
    entireYearFullTimeText: "Full-time, entire year",
    multiplePeriodText: "Multiple periods with varying hours",
    runwayWithLabel: "Runway with $1M"
  },

  // Login Screen translations
  loginScreen: {
    title: "hopingwegetfunded.com",
    subtitle: "Enter password to access the startup success calculator",
    passwordPlaceholder: "Password",
    loginButtonText: "Login",
    incorrectPasswordMessage: "Incorrect password!"
  },

  // Configuration IO translations
  configIO: {
    title: "Configuration Management",
    exportButton: "Export Configuration",
    importButton: "Import Configuration",
    resetButton: "Reset to Defaults",
    exportSuccess: "Configuration exported successfully",
    importSuccess: "Configuration imported successfully",
    importError: "Error importing configuration",
    resetSuccess: "Configuration reset to defaults",
    fileName: "startup-config.json"
  }
};

export default english;